import { useId, useMemo, useState, type MouseEvent, type ReactNode } from 'react'
import { Button } from './Button'
import { OverlayPendingContext } from './form-context'
import { overlayBackdropTransition, overlayPanelTransition, useEnterTransition, useOverlay } from './use-overlay'

export type OverlayClosePolicy = 'always' | 'not-while-pending' | 'confirm'

export type DialogProps = {
  open: boolean
  title: string
  description?: string
  /** 显式 aria-describedby，指向调用方自管的描述节点；优先于 description 字符串的自动接线 */
  ariaDescribedBy?: string
  onClose: () => void
  children: ReactNode
  footer?: ReactNode
  className?: string
  /** 提交中 Escape / 遮罩 / × 是否关闭。默认 not-while-pending。 */
  closePolicy?: OverlayClosePolicy
  pending?: boolean
}

export function Dialog({
  open,
  title,
  description,
  ariaDescribedBy,
  onClose,
  children,
  footer,
  className = '',
  closePolicy = 'not-while-pending',
  pending: pendingProp = false
}: DialogProps) {
  const titleId = useId()
  const descriptionId = useId()
  const [childPending, setChildPending] = useState(false)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const pending = pendingProp || childPending
  const overlayValue = useMemo(
    () => ({ pending, setChildPending }),
    [pending]
  )
  const requestClose = () => {
    if (closePolicy === 'not-while-pending' && pending) return
    if (closePolicy === 'confirm') {
      setConfirmOpen(true)
      return
    }
    onClose()
  }
  const dialogRef = useOverlay<HTMLElement>(open, requestClose)
  const entered = useEnterTransition(open)
  if (!open) return null

  const closeFromBackdrop = (event: MouseEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget) requestClose()
  }
  return (
    // z-50：遮罩+面板层（分层约定见 use-overlay.ts）
    <div className={`fixed inset-0 z-50 flex items-end justify-center bg-[var(--color-overlay)] backdrop-blur-sm sm:items-center sm:p-7 ${overlayBackdropTransition} ${entered ? 'opacity-100' : 'opacity-0'}`} onMouseDown={closeFromBackdrop}>
      <section
        ref={dialogRef}
        className={`flex max-h-[92dvh] w-full flex-col overflow-hidden rounded-t-[var(--radius-lg)] bg-[var(--color-panel)] shadow-[var(--shadow-lg)] sm:max-h-[calc(100dvh-56px)] sm:rounded-[var(--radius-lg)] ${/(?:^|\s)max-w-/.test(className) ? '' : 'max-w-2xl'} ${overlayPanelTransition} ${entered ? '' : 'translate-y-2 opacity-0'} ${className}`.trim()}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={ariaDescribedBy ?? (description ? descriptionId : undefined)}
      >
        {/* 头脚固定、正文区滚动（与 Modal 同一结构），长表单不丢标题与操作 */}
        <div className="flex shrink-0 items-start justify-between gap-4 border-b border-[var(--color-line)] px-5 py-4">
          <div className="min-w-0">
            <h2 id={titleId} className="m-0 text-lg font-semibold text-[var(--color-ink)]">{title}</h2>
            {description ? <p id={descriptionId} className="m-0 mt-1 text-xs text-[var(--color-muted)]">{description}</p> : null}
          </div>
          <Button variant="icon" onClick={requestClose} aria-label="关闭对话框">×</Button>
        </div>
        <OverlayPendingContext.Provider value={overlayValue}>
          <div className="min-h-0 flex-1 overflow-y-auto">{children}</div>
          {footer ? <div className="flex shrink-0 items-center justify-end gap-2 border-t border-[var(--color-line)] px-5 py-3">{footer}</div> : null}
        </OverlayPendingContext.Provider>
      </section>
      {confirmOpen ? (
        <div className="fixed inset-0 z-[60] grid place-items-center bg-[var(--color-overlay)] p-4">
          <div role="alertdialog" aria-labelledby={`${titleId}-close-confirm`} className="w-full max-w-sm rounded-[var(--radius-lg)] border border-[var(--color-line)] bg-[var(--color-panel)] p-5 shadow-[var(--shadow-lg)]">
            <p id={`${titleId}-close-confirm`} className="m-0 text-sm font-medium text-[var(--color-ink)]">确认关闭？</p>
            <p className="m-0 mt-1 text-xs text-[var(--color-muted)]">未完成的操作将被中断。</p>
            <div className="mt-4 flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setConfirmOpen(false)}>继续</Button>
              <Button variant="danger" onClick={() => { setConfirmOpen(false); onClose() }}>确认关闭</Button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  )
}

export function DialogBody({ children }: { children: ReactNode }) {
  return <div className="px-5 py-5">{children}</div>
}

export function DialogActions({ children }: { children: ReactNode }) {
  return <div className="mt-5 flex justify-end gap-2">{children}</div>
}
